import { Html5QrcodeCameraScanConfig } from 'html5-qrcode/esm/html5-qrcode';
import React, { useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';

import {
  BottomLeftCorner,
  TopLeftCorner,
  TopRightCorner,
  BottomRightCorner,
  Close
} from '@williamhill/rosi-ui-shared/src/assets/icons/peripherals/camera';
import useCodeScanner from './use-code-scanner';
import { Html5QrcodeResult } from 'html5-qrcode/esm/core';
import { Html5QrcodeSupportedFormats } from 'html5-qrcode';
import { isSafari } from 'react-device-detect';
import { fillHeightForMobile } from '@williamhill/rosi-ui-shared/src/global-style';
import { tryParseTicketSearchUrl, useTicketSearchNavigation } from '../navigation';
import { getLogger } from '@williamhill/rosi-ui-shared/src/lib/logger-plus';
import { Block } from '@williamhill/rosi-ui-shared/src/components/typography';

const logger = getLogger('bet-tracker/scan-bet');
const scannerAreaId = 'scanner';

const cameraConfig: Html5QrcodeCameraScanConfig = {
  fps: 10,
  videoConstraints: { facingMode: 'environment' }
};

export default function ScanBetComponent() {
  const cameraContainerRef = useRef<HTMLDivElement>(null);
  const { goToTicketSearch, goToHome } = useTicketSearchNavigation();
  const { startScanner, stopScanner } = useCodeScanner({
    elementId: scannerAreaId,
    onSuccessfulScan: handleSuccessfulScan,
    onScanError: () => {}
  });

  useEffect(() => {
    if (cameraContainerRef.current) {
      const { width, height } = cameraContainerRef.current.getBoundingClientRect();
      const aspectRatio = height / width;
      startScanner({
        cameraConfig: { ...cameraConfig, videoConstraints: { ...cameraConfig.videoConstraints, aspectRatio } }
      });
    }
  }, [cameraContainerRef, startScanner]);

  return (
    <Container>
      <CameraContainer ref={cameraContainerRef}>
        <div id={scannerAreaId} />
        <TopLeft>
          <TopLeftCorner />
        </TopLeft>
        <TopRight>
          <TopRightCorner />
        </TopRight>
        <BottomRight>
          <BottomRightCorner />
        </BottomRight>
        <BottomLeft>
          <BottomLeftCorner />
        </BottomLeft>
      </CameraContainer>
      <CloseContainer>
        <CloseButton weight="bold" onClick={handleClose}>
          <Close />
          Close
        </CloseButton>
      </CloseContainer>
    </Container>
  );

  async function handleClose() {
    await stopScanner();
    goToHome();
  }

  async function handleSuccessfulScan(decodedText: string, result: Html5QrcodeResult) {
    logger.info('Scanned ticket', { decodedText, result });
    await stopScanner();
    if (result.result.format?.format === Html5QrcodeSupportedFormats.QR_CODE) {
      const retailTicketId = tryParseTicketSearchUrl(decodedText);
      if (retailTicketId) {
        goToTicketSearch({ ticketNumber: retailTicketId, origin: 'scan-bet' });
        logger.info(`Scanned ticket with id '${retailTicketId}', navigating to ticket search.`);
      } else {
        logger.info(`Couldn't parse id in '${decodedText}', staying on scan page.`);
      }
    } else {
      goToTicketSearch({ ticketNumber: decodedText, origin: 'scan-bet' });
    }
  }
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  ${isSafari
    ? fillHeightForMobile
    : css`
        height: 100%;
      `}

  color: ${({ theme }) => theme.colors.white};
  position: relative;
`;

const CameraContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.components.mobile.scan.background};
  position: relative;
  flex-grow: 1;
`;

const CloseContainer = styled.footer`
  background-color: ${({ theme }) => theme.colors.black};
  height: 6.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CloseButton = styled(Block)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  flex-direction: column;
`;

const BottomLeft = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
`;

const TopLeft = styled.div`
  position: absolute;
  top: 0;
  left: 0;
`;

const BottomRight = styled.div`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const TopRight = styled.div`
  position: absolute;
  top: 0;
  right: 0;
`;
